import { useMemo, useState } from 'react'

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'

import styles from './EventsTable.module.scss'
import sharedStyles from '@/pages/app/AnalyticsPortal/styles.module.scss'

import type { AnalyticsEventStatus, AnalyticsLifecycleState } from '@/api/generated/api'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import {
  LIFECYCLE_STATES,
  PLATFORMS,
  STATUS_CLASS_MAP,
  STATUS_VALUES,
} from '@/pages/app/AnalyticsPortal/constants'
import type { AnalyticsEvent, EventsTableFilters } from '@/pages/app/AnalyticsPortal/types'
import {
  filterEventsByFilters,
  filterEventsBySearch,
  mergeEventsWithPendingChanges,
} from '@/pages/app/AnalyticsPortal/utils'

interface EventsTableProps {
  events: AnalyticsEvent[]
  isLoading: boolean
  onEditEvent: (event: AnalyticsEvent) => void
  onCreateEvent: () => void
  onDeprecateEvent: (event: AnalyticsEvent) => void
  onUndeprecateEvent: (event: AnalyticsEvent) => void
  pendingChanges: Map<string, Partial<AnalyticsEvent>>
}

const SORT_FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  modified_at: 'Last Updated',
  lifecycle_state: 'Lifecycle',
  status: 'Status',
}

const LIFECYCLE_ORDER: Record<AnalyticsLifecycleState, number> = {
  draft: 0,
  active: 1,
  deprecated: 2,
}

const STATUS_ORDER: Record<AnalyticsEventStatus, number> = {
  needs_publish: 0,
  not_implemented: 1,
  implemented: 2,
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const columnHelper = createColumnHelper<AnalyticsEvent>()

const EventsTable = ({
  events,
  isLoading,
  onEditEvent,
  onCreateEvent,
  onDeprecateEvent,
  onUndeprecateEvent,
  pendingChanges,
}: EventsTableProps) => {
  const [viewingSchema, setViewingSchema] = useState<{ name: string; schema: Record<string, unknown> } | null>(null)
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }])
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState<EventsTableFilters>({
    lifecycle: [],
    status: [],
    platform: [],
  })

  const hasActiveFilters = filters.lifecycle.length > 0 || filters.status.length > 0 || filters.platform.length > 0

  const toggleFilter = (type: keyof EventsTableFilters, value: string) => {
    setFilters((prev) => {
      const currentValues = prev[type] as string[]
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value]
      return { ...prev, [type]: newValues }
    })
  }

  const clearFilters = () => {
    setFilters({ lifecycle: [], status: [], platform: [] })
  }

  const data = useMemo(() => {
    const merged = mergeEventsWithPendingChanges(events, pendingChanges)
    const searched = filterEventsBySearch(merged, searchQuery)
    return filterEventsByFilters(searched, filters)
  }, [events, pendingChanges, searchQuery, filters])

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => (
          <span className={sharedStyles.truncatedText} title={info.getValue()}>
            {info.getValue() || '(unnamed)'}
          </span>
        ),
        sortingFn: 'alphanumeric',
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: (info) => (
          <span className={sharedStyles.truncatedText} title={info.getValue() || ''}>
            {info.getValue() || '-'}
          </span>
        ),
        enableSorting: false,
      }),
      columnHelper.accessor('trigger', {
        header: 'Trigger',
        cell: (info) => (
          <span className={sharedStyles.truncatedText} title={info.getValue() || ''}>
            {info.getValue() || '-'}
          </span>
        ),
        enableSorting: false,
      }),
      columnHelper.accessor('properties_schema', {
        header: 'Properties Schema',
        cell: (info) => {
          const event = info.row.original
          const schema = info.getValue() ?? {}
          if (Object.keys(schema).length === 0) {
            return <span className={styles.emptySchema}>-</span>
          }
          return (
            <button
              className={`${sharedStyles.actionButton} ${styles.viewButton}`}
              onClick={() =>
                setViewingSchema({
                  name: event.name,
                  schema: schema as Record<string, unknown>,
                })
              }
            >
              View
            </button>
          )
        },
        enableSorting: false,
      }),
      columnHelper.accessor('platforms', {
        header: 'Platforms',
        cell: (info) => {
          const platforms = info.getValue() ?? []
          return (
            <div className={styles.platformTags}>
              {platforms.length > 0 ? (
                platforms.map((p) => (
                  <span key={p} className={styles.platformTag}>
                    {p}
                  </span>
                ))
              ) : (
                <span className={styles.platformTag}>none</span>
              )}
            </div>
          )
        },
        enableSorting: false,
      }),
      columnHelper.accessor('lifecycle_state', {
        header: 'Lifecycle',
        cell: (info) => {
          const lifecycle = info.row.original.lifecycle_state ?? 'draft'
          return (
            <span className={`${sharedStyles.lifecycleBadge} ${sharedStyles[lifecycle]}`}>
              {lifecycle}
            </span>
          )
        },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.lifecycle_state ?? 'draft'
          const b = rowB.original.lifecycle_state ?? 'draft'
          return LIFECYCLE_ORDER[a] - LIFECYCLE_ORDER[b]
        },
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => (
          <span className={`${sharedStyles.statusBadge} ${sharedStyles[STATUS_CLASS_MAP[info.getValue()]]}`}>
            {info.getValue().replace(/_/g, ' ')}
          </span>
        ),
        sortingFn: (rowA, rowB) => {
          return STATUS_ORDER[rowA.original.status] - STATUS_ORDER[rowB.original.status]
        },
      }),
      columnHelper.accessor('updated_by_name', {
        header: 'Updated By',
        cell: (info) => {
          const event = info.row.original
          const displayName = info.getValue() || event.updated_by || '-'
          return (
            <span className={sharedStyles.truncatedText} title={displayName}>
              {displayName}
            </span>
          )
        },
        enableSorting: false,
      }),
      columnHelper.accessor('modified_at', {
        header: 'Last Updated',
        cell: (info) => (
          <span className={styles.dateText}>{info.getValue() ? formatDate(info.getValue()) : '-'}</span>
        ),
        sortingFn: (rowA, rowB) => {
          return new Date(rowA.original.modified_at).getTime() - new Date(rowB.original.modified_at).getTime()
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const event = info.row.original
          const pendingChange = pendingChanges.get(event.id)
          const originalEvent = events.find((e) => e.id === event.id)
          const originalLifecycle = originalEvent?.lifecycle_state
          const isDeprecatedByPending =
            pendingChange?.lifecycle_state === 'deprecated' && originalLifecycle !== 'deprecated'
          const isOriginallyDeprecated = originalLifecycle === 'deprecated'

          return (
            <div className={sharedStyles.actionButtons}>
              <button className={sharedStyles.actionButton} onClick={() => onEditEvent(event)}>
                Edit
              </button>
              {!isOriginallyDeprecated &&
                (isDeprecatedByPending ? (
                  <button
                    className={`${sharedStyles.actionButton} ${sharedStyles.undeprecate}`}
                    onClick={() => onUndeprecateEvent(event)}
                  >
                    Undeprecate
                  </button>
                ) : (
                  <button
                    className={`${sharedStyles.actionButton} ${sharedStyles.deprecate}`}
                    onClick={() => onDeprecateEvent(event)}
                  >
                    Deprecate
                  </button>
                ))}
            </div>
          )
        },
      }),
    ],
    [pendingChanges, events, onEditEvent, onDeprecateEvent, onUndeprecateEvent],
  )

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const currentSort = sorting[0]
  const sortLabel = currentSort ? SORT_FIELD_LABELS[currentSort.id] || currentSort.id : 'Name'

  if (isLoading) {
    return (
      <div className={sharedStyles.tableContainer}>
        <div className={sharedStyles.loadingState}>Loading events...</div>
      </div>
    )
  }

  return (
    <div className={sharedStyles.tableContainer}>
      <div className={sharedStyles.tableControls}>
        <div className={styles.tableTitleSection}>
          <span className={styles.tableTitle}>Analytics Events</span>
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>
        <div className={sharedStyles.filters}>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button className={styles.controlButton}>
                Sort: {sortLabel} {currentSort?.desc ? '↓' : '↑'}
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className={styles.dropdown} align="end" sideOffset={4}>
                {Object.entries(SORT_FIELD_LABELS).map(([field, label]) => (
                  <DropdownMenu.Item
                    key={field}
                    className={`${styles.dropdownItem} ${currentSort?.id === field ? styles.active : ''}`}
                    onSelect={() => {
                      if (currentSort?.id === field) {
                        setSorting([{ id: field, desc: !currentSort.desc }])
                      } else {
                        setSorting([{ id: field, desc: false }])
                      }
                    }}
                  >
                    {label}
                    {currentSort?.id === field && (
                      <span className={styles.sortIndicator}>{currentSort.desc ? '↓' : '↑'}</span>
                    )}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>

          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button className={`${styles.controlButton} ${hasActiveFilters ? styles.hasFilters : ''}`}>
                Filter{' '}
                {hasActiveFilters && `(${filters.lifecycle.length + filters.status.length + filters.platform.length})`}
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className={`${styles.dropdown} ${styles.filterDropdown}`} align="end" sideOffset={4}>
                <div className={styles.filterSection}>
                  <div className={styles.filterSectionTitle}>Lifecycle</div>
                  {LIFECYCLE_STATES.map((state) => (
                    <DropdownMenu.CheckboxItem
                      key={state}
                      className={styles.filterOption}
                      checked={filters.lifecycle.includes(state)}
                      onCheckedChange={() => toggleFilter('lifecycle', state)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      <DropdownMenu.ItemIndicator className={styles.checkboxIndicator}>✓</DropdownMenu.ItemIndicator>
                      <span>{state}</span>
                    </DropdownMenu.CheckboxItem>
                  ))}
                </div>
                <div className={styles.filterSection}>
                  <div className={styles.filterSectionTitle}>Status</div>
                  {STATUS_VALUES.map((status) => (
                    <DropdownMenu.CheckboxItem
                      key={status}
                      className={styles.filterOption}
                      checked={filters.status.includes(status)}
                      onCheckedChange={() => toggleFilter('status', status)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      <DropdownMenu.ItemIndicator className={styles.checkboxIndicator}>✓</DropdownMenu.ItemIndicator>
                      <span>{status.replace(/_/g, ' ')}</span>
                    </DropdownMenu.CheckboxItem>
                  ))}
                </div>
                <div className={styles.filterSection}>
                  <div className={styles.filterSectionTitle}>Platform</div>
                  {PLATFORMS.map((platform) => (
                    <DropdownMenu.CheckboxItem
                      key={platform}
                      className={styles.filterOption}
                      checked={filters.platform.includes(platform)}
                      onCheckedChange={() => toggleFilter('platform', platform)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      <DropdownMenu.ItemIndicator className={styles.checkboxIndicator}>✓</DropdownMenu.ItemIndicator>
                      <span>{platform}</span>
                    </DropdownMenu.CheckboxItem>
                  ))}
                </div>
                {hasActiveFilters && (
                  <DropdownMenu.Item className={styles.clearFilters} onSelect={clearFilters}>
                    Clear all filters
                  </DropdownMenu.Item>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>

          <Button title="Add Event" onClick={onCreateEvent} type="dark" />
        </div>
      </div>

      {table.getRowModel().rows.length === 0 ? (
        <div className={sharedStyles.emptyState}>
          {searchQuery || hasActiveFilters
            ? 'No events match your search or filters.'
            : "No events yet. Click 'Add Event' to create one."}
        </div>
      ) : (
        <div className={sharedStyles.tableWrapper}>
          <table className={`${sharedStyles.table} ${styles.resizableTable}`} style={{ minWidth: table.getTotalSize() }}>
            <thead className={sharedStyles.tableHeader}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{ width: header.getSize() }}
                      className={styles.resizableHeader}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={`${styles.resizer} ${header.column.getIsResizing() ? styles.isResizing : ''}`}
                      />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className={sharedStyles.tableBody}>
              {table.getRowModel().rows.map((row) => {
                const event = row.original
                const originalEvent = events.find((e) => e.id === event.id)
                const isOriginallyDeprecated = originalEvent?.lifecycle_state === 'deprecated'

                const rowClasses =
                  [
                    pendingChanges.has(event.id) ? styles.hasChanges : '',
                    isOriginallyDeprecated ? sharedStyles.deprecatedRow : '',
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined

                return (
                  <tr key={row.id} className={rowClasses}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {viewingSchema && (
        <DialogWrapper
          isOpen={true}
          title={`Properties Schema: ${viewingSchema.name}`}
          close={() => setViewingSchema(null)}
          minWidth={560}
        >
          <div className={sharedStyles.dialogBody}>
            <pre className={styles.jsonViewer}>{JSON.stringify(viewingSchema.schema, null, 2)}</pre>
            <div className={sharedStyles.dialogFooter}>
              <Button title="Close" onClick={() => setViewingSchema(null)} type="light" />
            </div>
          </div>
        </DialogWrapper>
      )}
    </div>
  )
}

export default EventsTable
